import { type BaseSortState } from 'noddi-types';
import { stringify } from 'query-string';

import { convertObjectKeysToSnakeCase } from '../keyConversion';

/**
 * Shared URL/query-string infrastructure for the resource layer. Lives at the
 * `resources/` root (not inside any resource folder) because every resource and
 * `defineResource` depends on it — see also `defineResource.ts` and `resources.ts`.
 */

export const apiVersion = 'v1/';

export type ResponseType = 'json' | 'blob' | 'text' | 'arraybuffer';

// Repeated id keys count as one Django field each. Past 1000 fields the
// request is rejected before the view runs. A long id list is one value.
const MAX_REPEATED_ID_VALUES = 100;

const isIntegerId = (value: unknown): boolean => {
  if (typeof value === 'number') {
    return Number.isInteger(value);
  }
  return typeof value === 'string' && /^-?\d+$/.test(value);
};

const collapseLongIdLists = (params: Record<string, unknown>): Record<string, unknown> => {
  const collapsed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (
      key.endsWith('ids') &&
      Array.isArray(value) &&
      value.length > MAX_REPEATED_ID_VALUES &&
      value.every(isIntegerId)
    ) {
      collapsed[key] = value.map(String).join(',');
      continue;
    }
    collapsed[key] = value;
  }
  return collapsed;
};

const isBaseSortState = (value: unknown): value is BaseSortState => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const { column, order } = value as Partial<BaseSortState>;
  const isValidOrder = order === 'asc' || order === 'desc' || order === null;

  return typeof column === 'string' && isValidOrder;
};

const serializeOrderingParams = (orderingParam: unknown): string[] | undefined => {
  if (!Array.isArray(orderingParam)) {
    return undefined;
  }

  const serialized = orderingParam
    .filter(isBaseSortState)
    .map(({ column, order }) => {
      if (!order || !column) {
        return null;
      }
      // The column is already the endpoint's own `ordering` value (a column
      // declares it as `meta.orderingKey`), so it is sent verbatim: converting
      // it here would let a camelCase key look like it works.
      return `${order === 'desc' ? '-' : ''}${column}`;
    })
    .filter((value): value is string => Boolean(value));

  return serialized.length ? serialized : undefined;
};

export function getNoddiUrl({
  queryParams,
  path
}: {
  queryParams?: Record<string, unknown> | unknown;
  path: string;
}): string {
  // filter out empty params (preserve 0 values)
  const filteredQueryParams = Object.entries((queryParams as Record<string, unknown>) || {}).reduce<
    Record<string, unknown>
  >((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});

  const filteredQueryParamsWithOrdering = (() => {
    if (!('order' in filteredQueryParams)) {
      return filteredQueryParams;
    }

    const orderingValues = serializeOrderingParams(filteredQueryParams.order);
    // Remove 'order' from params first to avoid sending both 'order=[object Object]' and 'ordering=column_name'.
    // The backend expects 'ordering' (string), not 'order' (array of objects).
    const { order: _, ...rest } = filteredQueryParams;

    if (orderingValues) {
      return {
        ...rest,
        ordering: orderingValues
      };
    }

    return rest;
  })();

  const snakeCaseParams = convertObjectKeysToSnakeCase(filteredQueryParamsWithOrdering || {}) as Record<
    string,
    unknown
  >;
  const queryParamsForUrl = collapseLongIdLists(snakeCaseParams);

  const _queryParams = stringify(queryParamsForUrl, { arrayFormat: 'none' });
  const queryParamString = _queryParams ? `?${_queryParams}` : '';

  return `/${path}${queryParamString}`;
}
